import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, deleteDoc, doc, updateDoc, where, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Edit, Trash2, Eye, EyeOff, Calendar, Tag, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

// Sample blog posts for seeding
const sampleBlogPosts = [
  {
    id: 'esg-reporting-africa-2025',
    title: 'ESG Reporting in Africa: Navigating the Regulatory Landscape in 2025',
    slug: 'esg-reporting-africa-regulatory-landscape-2025',
    excerpt: 'A comprehensive guide to understanding ESG reporting requirements across African markets, from JSE Listings Requirements to emerging frameworks in Nigeria, Kenya, and Egypt.',
    content: '<h2>The Evolution of ESG Reporting in Africa</h2><p>Environmental, Social, and Governance (ESG) reporting has rapidly evolved from a voluntary practice to a regulatory requirement across African markets.</p><h3>Key Regulatory Frameworks</h3><p><strong>South Africa</strong> leads the continent with the JSE Listings Requirements mandating integrated reporting. <strong>Nigeria</strong> has introduced Sustainable Banking Principles, while <strong>Kenya</strong> has implemented Capital Markets Sustainable Finance Guidelines.</p>',
    author: 'ESGnet Team',
    category: 'Regulatory',
    tags: ['ESG', 'Reporting', 'Africa', 'Compliance'],
    status: 'published',
    featuredImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800',
    publishedAt: new Date(),
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: 'carbon-accounting-mining',
    title: 'Carbon Accounting for African Mining Companies: A Practical Guide',
    slug: 'carbon-accounting-african-mining-practical-guide',
    excerpt: 'Learn how to implement effective carbon accounting practices in the mining sector, with specific focus on Scope 1, 2, and 3 emissions tracking.',
    content: '<h2>Understanding Carbon Accounting in Mining</h2><p>The mining sector faces unique challenges in carbon accounting due to the energy-intensive nature of operations and complex supply chains.</p><h3>Scope 1 Emissions</h3><p>Direct emissions from owned or controlled sources including diesel combustion in haul trucks and generators.</p><h3>Scope 2 Emissions</h3><p>Indirect emissions from purchased electricity, which is often the largest source for mining operations.</p>',
    author: 'ESGnet Team',
    category: 'Carbon',
    tags: ['Carbon', 'Mining', 'Emissions', 'GHG'],
    status: 'published',
    featuredImage: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800',
    publishedAt: new Date(Date.now() - 86400000),
    createdAt: new Date(Date.now() - 86400000),
    updatedAt: new Date(Date.now() - 86400000)
  },
  {
    id: 'tcfd-implementation-financial',
    title: 'TCFD Implementation for African Financial Institutions',
    slug: 'tcfd-implementation-african-financial-institutions',
    excerpt: 'Lessons from early adopters and a roadmap for implementing Task Force on Climate-related Financial Disclosures in African banks and insurers.',
    content: '<h2>Why TCFD Matters for African Financial Institutions</h2><p>Climate risk poses significant threats to financial stability in Africa, from physical risks like droughts to transition risks from policy changes.</p><h3>The Four Pillars of TCFD</h3><ul><li><strong>Governance:</strong> Board oversight and management\'s role</li><li><strong>Strategy:</strong> Climate risks and opportunities</li><li><strong>Risk Management:</strong> Processes for identifying and managing climate risks</li><li><strong>Metrics & Targets:</strong> Quantified climate-related metrics</li></ul>',
    author: 'ESGnet Team',
    category: 'Climate',
    tags: ['TCFD', 'Climate', 'Banking', 'Risk'],
    status: 'published',
    featuredImage: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800',
    publishedAt: new Date(Date.now() - 172800000),
    createdAt: new Date(Date.now() - 172800000),
    updatedAt: new Date(Date.now() - 172800000)
  },
  {
    id: 'sustainable-supply-chains',
    title: 'Building Sustainable Supply Chains in Africa: A Strategic Approach',
    slug: 'sustainable-supply-chains-africa-strategic-approach',
    excerpt: 'How African businesses can transform their supply chains to meet international sustainability standards while supporting local communities.',
    content: '<h2>The Business Case for Sustainable Supply Chains</h2><p>International buyers increasingly require sustainability credentials from African suppliers. From agriculture to manufacturing, supply chain transparency is becoming a competitive advantage.</p><h3>Key Focus Areas</h3><ul><li><strong>Traceability:</strong> Track products from source to consumer</li><li><strong>Labor Standards:</strong> Ensure fair wages and working conditions</li><li><strong>Environmental Impact:</strong> Reduce water usage, emissions, and waste</li><li><strong>Community Engagement:</strong> Support local development initiatives</li></ul><h3>Technology Solutions</h3><p>Blockchain and IoT are enabling real-time supply chain monitoring, making it easier to verify sustainability claims and build trust with buyers.</p>',
    author: 'ESGnet Team',
    category: 'Supply Chain',
    tags: ['Supply Chain', 'Sustainability', 'Traceability', 'Africa'],
    status: 'published',
    featuredImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800',
    publishedAt: new Date(Date.now() - 259200000),
    createdAt: new Date(Date.now() - 259200000),
    updatedAt: new Date(Date.now() - 259200000)
  },
  {
    id: 'water-stewardship-agriculture',
    title: 'Water Stewardship in African Agriculture: Best Practices for 2025',
    slug: 'water-stewardship-african-agriculture-best-practices',
    excerpt: 'Practical strategies for agricultural businesses to optimize water use, reduce waste, and build resilience against climate-related water stress.',
    content: '<h2>Water: Africa\'s Critical Resource</h2><p>With agriculture consuming 70% of freshwater resources, sustainable water management is essential for African farming operations.</p><h3>Water Efficiency Strategies</h3><ul><li><strong>Drip Irrigation:</strong> Reduce water consumption by up to 60%</li><li><strong>Rainwater Harvesting:</strong> Capture and store seasonal rainfall</li><li><strong>Soil Moisture Monitoring:</strong> Optimize irrigation timing with sensors</li><li><strong>Drought-Resistant Varieties:</strong> Select crops suited to local conditions</li></ul><h3>Reporting Requirements</h3><p>Many international frameworks now require water disclosure. ESGnet helps track water withdrawal, consumption, and discharge across operations.</p>',
    author: 'ESGnet Team',
    category: 'Water',
    tags: ['Water', 'Agriculture', 'Irrigation', 'Climate'],
    status: 'published',
    featuredImage: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=800',
    publishedAt: new Date(Date.now() - 345600000),
    createdAt: new Date(Date.now() - 345600000),
    updatedAt: new Date(Date.now() - 345600000)
  },
  {
    id: 'biodiversity-impact-assessment',
    title: 'Biodiversity Impact Assessment: A Guide for African Businesses',
    slug: 'biodiversity-impact-assessment-african-businesses',
    excerpt: 'Understanding and managing biodiversity impacts is becoming critical for businesses operating near sensitive ecosystems in Africa.',
    content: '<h2>Why Biodiversity Matters</h2><p>Africa\'s rich biodiversity faces increasing pressure from economic development. Businesses have both regulatory obligations and reputational incentives to minimize negative impacts.</p><h3>Assessment Framework</h3><ul><li><strong>Baseline Studies:</strong> Document existing flora and fauna</li><li><strong>Impact Identification:</strong> Assess potential negative effects</li><li><strong>Mitigation Hierarchy:</strong> Avoid, minimize, restore, offset</li><li><strong>Monitoring:</strong> Track biodiversity indicators over time</li></ul><h3>TNFD Alignment</h3><p>The Taskforce on Nature-related Financial Disclosures (TNFD) provides a framework for nature-related risk disclosure, building on TCFD principles.</p>',
    author: 'ESGnet Team',
    category: 'Biodiversity',
    tags: ['Biodiversity', 'TNFD', 'Environment', 'Conservation'],
    status: 'published',
    featuredImage: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800',
    publishedAt: new Date(Date.now() - 432000000),
    createdAt: new Date(Date.now() - 432000000),
    updatedAt: new Date(Date.now() - 432000000)
  },
  {
    id: 'social-impact-measurement',
    title: 'Measuring Social Impact: Frameworks for African Enterprises',
    slug: 'social-impact-measurement-frameworks-african-enterprises',
    excerpt: 'How to effectively measure and report on social impact, from job creation and skills development to community health and education outcomes.',
    content: '<h2>The Social Dimension of ESG</h2><p>While environmental metrics often receive more attention, social impact measurement is equally important for African businesses seeking investment and partnerships.</p><h3>Key Social Metrics</h3><ul><li><strong>Employment:</strong> Jobs created, local hiring percentage, wage levels</li><li><strong>Skills Development:</strong> Training hours, certifications, career progression</li><li><strong>Community Investment:</strong> Local procurement, social programs, infrastructure</li><li><strong>Health & Safety:</strong> Incident rates, wellness programs, COVID-19 response</li></ul><h3>Standardized Frameworks</h3><p>IRIS+, GRI, and SASB provide standardized metrics for social impact reporting. ESGnet supports all major frameworks to streamline your disclosure process.</p>',
    author: 'ESGnet Team',
    category: 'Social',
    tags: ['Social Impact', 'Community', 'Employment', 'SDGs'],
    status: 'published',
    featuredImage: 'https://images.unsplash.com/photo-1531206715517-5c0ba140b2b8?w=800',
    publishedAt: new Date(Date.now() - 518400000),
    createdAt: new Date(Date.now() - 518400000),
    updatedAt: new Date(Date.now() - 518400000)
  },
  {
    id: 'green-bonds-africa',
    title: 'Green Bonds in Africa: Financing the Sustainable Transition',
    slug: 'green-bonds-africa-financing-sustainable-transition',
    excerpt: 'An overview of the growing green bond market in Africa, with insights on issuance, certification, and investor expectations.',
    content: '<h2>The Rise of Green Finance in Africa</h2><p>Green bonds have emerged as a key instrument for financing climate-positive projects in Africa, from renewable energy to sustainable infrastructure.</p><h3>Market Overview</h3><p>Since the first African green bond in 2014, issuance has grown significantly. South Africa, Kenya, Nigeria, and Egypt have all seen successful green bond issues.</p><h3>Certification Standards</h3><ul><li><strong>Climate Bonds Standard:</strong> The most rigorous certification</li><li><strong>ICMA Green Bond Principles:</strong> Widely adopted guidelines</li><li><strong>National Standards:</strong> Country-specific frameworks emerging</li></ul><h3>Use of Proceeds</h3><p>Common uses include renewable energy projects, green buildings, sustainable transport, and climate adaptation infrastructure.</p>',
    author: 'ESGnet Team',
    category: 'Finance',
    tags: ['Green Bonds', 'Finance', 'Investment', 'Climate'],
    status: 'published',
    featuredImage: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800',
    publishedAt: new Date(Date.now() - 604800000),
    createdAt: new Date(Date.now() - 604800000),
    updatedAt: new Date(Date.now() - 604800000)
  }
];

const CMSBlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load posts
      const postsQuery = query(
        collection(db, 'cms_blog_posts'),
        orderBy('createdAt', 'desc')
      );
      const postsSnapshot = await getDocs(postsQuery);
      const postsData = postsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);

      // Load categories
      const categoriesQuery = query(collection(db, 'cms_blog_categories'));
      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, 'cms_blog_posts', postToDelete.id));
      toast.success('Post deleted successfully');
      setDeleteDialogOpen(false);
      setPostToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const toggleStatus = async (post) => {
    try {
      const newStatus = post.status === 'published' ? 'draft' : 'published';
      await updateDoc(doc(db, 'cms_blog_posts', post.id), {
        status: newStatus,
        ...(newStatus === 'published' && !post.publishedAt ? { publishedAt: new Date() } : {})
      });
      toast.success(`Post ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const seedSamplePosts = async () => {
    try {
      toast.loading('Creating sample blog posts...');
      for (const post of sampleBlogPosts) {
        const { id, ...postData } = post;
        await setDoc(doc(db, 'cms_blog_posts', id), postData);
      }
      toast.dismiss();
      toast.success(`Created ${sampleBlogPosts.length} sample blog posts!`);
      loadData();
    } catch (error) {
      toast.dismiss();
      console.error('Error seeding posts:', error);
      toast.error('Failed to create sample posts: ' + error.message);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Uncategorized';
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || post.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || post.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Blog Posts</h1>
            <p className="text-muted-foreground mt-1">Manage your blog content</p>
          </div>
          <div className="flex gap-2">
            {posts.length === 0 && !loading && (
              <Button variant="outline" onClick={seedSamplePosts}>
                <Sparkles className="h-4 w-4 mr-2" />
                Seed Sample Posts
              </Button>
            )}
            <Button onClick={() => navigate('/cms/blog/new')}>
              <Plus className="h-4 w-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>

        {/* Filters & Search */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Posts</CardTitle>
                <CardDescription>
                  {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={statusFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('all')}
                >
                  All
                </Button>
                <Button
                  variant={statusFilter === 'published' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('published')}
                >
                  Published
                </Button>
                <Button
                  variant={statusFilter === 'draft' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter('draft')}
                >
                  Draft
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search posts by title or excerpt..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Published</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Loading posts...
                      </TableCell>
                    </TableRow>
                  ) : filteredPosts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <p className="text-muted-foreground">No posts found</p>
                          {searchTerm && (
                            <Button variant="link" onClick={() => setSearchTerm('')}>
                              Clear search
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPosts.map((post) => (
                      <TableRow key={post.id} className="group">
                        <TableCell>
                          <div>
                            <div className="font-medium">{post.title || 'Untitled'}</div>
                            {post.excerpt && (
                              <div className="text-sm text-muted-foreground line-clamp-1 mt-1">
                                {post.excerpt}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {getCategoryName(post.category)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                            {post.status || 'draft'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {post.publishedAt?.toDate?.()?.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/cms/blog/${post.id}`)}
                              title="Edit post"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleStatus(post)}
                              title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                            >
                              {post.status === 'published' ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setPostToDelete(post);
                                setDeleteDialogOpen(true);
                              }}
                              title="Delete post"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Post</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{postToDelete?.title}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CMSBlogPage;

